import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { formatApiError } from '../lib/api';
import { ArrowRight, Shield } from 'lucide-react';
import { Logo } from '../components/Logo';
import { destinationForRole as resolveDestination } from '../lib/authRoutes';

export default function Login() {
    const { login, verify2fa, user, loading: authLoading } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [step2, setStep2] = useState(null);
    const [totpCode, setTotpCode] = useState('');
    const [pendingDest, setPendingDest] = useState(null);
    const [pendingRole, setPendingRole] = useState(null);

    useEffect(() => {
        if (!pendingDest || authLoading || !user) return;
        const role = user.role || pendingRole;
        const dest = resolveDestination(role, pendingDest, { restaurantId: user.restaurant_id });
        nav(dest, { replace: true });
        setPendingDest(null);
        setPendingRole(null);
    }, [pendingDest, pendingRole, authLoading, user, nav]);

    const redirectByRole = (role, fromPath = loc.state?.from) => {
        setPendingRole(role);
        setPendingDest(fromPath || resolveDestination(role, null));
    };

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const data = await login(email, password);
            if (data.requires_2fa) {
                setStep2({
                    pendingToken: data.pending_token,
                    name: data.user?.name,
                    email: data.user?.email,
                });
                setTotpCode('');
                return;
            }
            redirectByRole(data.user.role);
        } catch (err) {
            setError(formatApiError(err));
        } finally { setLoading(false); }
    };

    const submit2fa = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const data = await verify2fa(step2.pendingToken, totpCode);
            redirectByRole(data.user.role);
        } catch (err) {
            setError(formatApiError(err));
        } finally { setLoading(false); }
    };

    const fill = (e, p) => { setEmail(e); setPassword(p); setStep2(null); };

    return (
        <div className="grain" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem' }}>
            <div style={{ width: '100%', maxWidth: 440 }} className="fade-in-up">
                <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'center' }}>
                    <Logo size="lg" />
                </div>

                {!step2 ? (
                    <>
                        <h1 className="headline" style={{ fontSize: 'clamp(1.8rem, 6vw, 2.4rem)', margin: 0, textAlign: 'center' }}>Bine ai revenit.</h1>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '2rem', textAlign: 'center' }}>Autentifică-te pentru a gestiona restaurantul tău.</p>
                        <form onSubmit={submit} className="panel" style={{ padding: '1.6rem' }}>
                            <div style={{ marginBottom: '1rem' }}>
                                <label className="label">Email</label>
                                <input className="input" type="email" required data-testid="login-email"
                                       value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nume@domeniu.ro" />
                            </div>
                            <div style={{ marginBottom: '0.4rem' }}>
                                <label className="label">Parolă</label>
                                <input className="input" type="password" required data-testid="login-password"
                                       value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                            </div>
                            <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
                                <Link to="/forgot-password" data-testid="goto-forgot-password"
                                      style={{ color: 'var(--text-muted)', fontSize: '0.82rem', textDecoration: 'none' }}>
                                    Am uitat parola
                                </Link>
                            </div>
                            {error && <div style={{ color: 'var(--neon-magenta)', fontSize: '0.88rem', marginBottom: '0.8rem' }} data-testid="login-error">{error}</div>}
                            <button type="submit" disabled={loading} className="btn btn-primary" style={{ width: '100%' }} data-testid="login-submit">
                                {loading ? <span className="spinner" /> : <>Autentificare <ArrowRight size={16} /></>}
                            </button>
                        </form>
                    </>
                ) : (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                            <Shield size={22} color="var(--neon-teal)" />
                            <h1 className="headline" style={{ fontSize: '2rem', margin: 0 }}>
                                Verificare 2FA
                            </h1>
                        </div>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '1.4rem' }}>
                            Salut {step2.name || step2.email}! Introdu codul de 6 cifre din aplicația Google Authenticator.
                        </p>
                        <form onSubmit={submit2fa} className="panel" style={{ padding: '1.6rem' }}>
                            <input
                                className="input"
                                inputMode="numeric"
                                maxLength={6}
                                autoFocus
                                required
                                value={totpCode}
                                onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                                placeholder="000000"
                                style={{ letterSpacing: '0.45em', textAlign: 'center', fontSize: '1.5rem', marginBottom: '1rem' }}
                                data-testid="login-2fa-code"
                            />
                            {error && <div style={{ color: 'var(--neon-magenta)', fontSize: '0.88rem', marginBottom: '0.8rem' }} data-testid="login-2fa-error">{error}</div>}
                            <button type="submit" disabled={loading || totpCode.length < 6} className="btn btn-primary" style={{ width: '100%' }} data-testid="login-2fa-submit">
                                {loading ? <span className="spinner" /> : <>Verifică <ArrowRight size={16} /></>}
                            </button>
                            <button type="button" className="btn btn-ghost btn-sm" style={{ width: '100%', marginTop: 8 }}
                                    onClick={() => { setStep2(null); setTotpCode(''); setError(''); }}>
                                ← Înapoi la login
                            </button>
                        </form>
                    </>
                )}

                {!step2 && process.env.REACT_APP_SHOW_DEMO === 'true' && (
                <div className="panel" style={{ marginTop: '1rem', background: 'rgba(255,255,255,0.02)' }}>
                    <div className="kicker" style={{ marginBottom: '0.7rem' }}>Conturi demo</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <button type="button" className="btn btn-secondary btn-sm" data-testid="demo-admin" onClick={() => fill('admin@demo.app', 'Demo123!')}>
                            🛠 Restaurant Admin — admin@demo.app
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm" data-testid="demo-waiter" onClick={() => fill('waiter@demo.app', 'Demo123!')}>
                            🧑‍🍳 Ospătar — waiter@demo.app
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm" data-testid="demo-kitchen" onClick={() => fill('kitchen@demo.app', 'Demo123!')}>
                            👨‍🍳 Bucătărie — kitchen@demo.app
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm" data-testid="demo-superadmin" onClick={() => fill('superadmin@tableflow.app', 'SuperAdmin123!')}>
                            ⚡ Super Admin — superadmin@tableflow.app
                        </button>
                    </div>
                </div>
                )}

                {!step2 && (
                <>
                    <div style={{ textAlign: 'center', marginTop: '1.4rem', color: 'var(--text-muted)' }}>
                        Nu ai cont? <Link to="/register" style={{ color: 'var(--amber)' }} data-testid="goto-register">Înregistrează-ți restaurantul</Link>
                    </div>
                    <div style={{ textAlign: 'center', marginTop: '0.7rem' }}>
                        <Link to="/pin" className="btn btn-ghost btn-sm" data-testid="goto-pin">⚡ Ospătar/Bucătărie? Login cu PIN</Link>
                    </div>
                </>
                )}
            </div>
        </div>
    );
}
